import React, { useEffect, useState, type FormEvent } from 'react';
import FeatherIcon from '../FeatherIcon';
import { hubSpotService } from '../../services/hubspotService';
import { INTEREST_OPTIONS, type InterestChoice } from '../../products/editions';

const CONTACT_EMAIL = 'info@pawsitivefuture.org';
/** One vote per browser: a returning visitor sees their recorded answer instead of the form. */
const STORAGE_KEY = 'pf-deck-interest';
const INPUT_CLASS =
  'w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const readRecorded = (): InterestChoice | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return INTEREST_OPTIONS.some((o) => o.value === value) ? (value as InterestChoice) : null;
  } catch {
    return null;
  }
};

const labelFor = (choice: InterestChoice): string => INTEREST_OPTIONS.find((o) => o.value === choice)?.label ?? choice;

/** A Buy soon click: which edition, and when, so a repeat click on the same edition still applies. */
export interface InterestPreset {
  readonly choice: InterestChoice;
  readonly at: number;
}

/**
 * Non-binding interest vote, posted to the HubSpot deck-interest form. Voting takes an email, so
 * HubSpot counts one vote per person; launch emails are a separate, optional opt-in. No payment,
 * no reservation: the copy says so next to the button. A build without that form configured shows
 * the form with submission closed rather than letting someone fill it in to watch it fail.
 */
const InterestForm: React.FC<{ preset: InterestPreset | null }> = ({ preset }) => {
  const [recorded, setRecorded] = useState<InterestChoice | null>(readRecorded);
  const [choice, setChoice] = useState<InterestChoice | null>(null);
  const [wantsUpdates, setWantsUpdates] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const open = hubSpotService.deckInterestConfigured;

  useEffect(() => {
    if (preset) setChoice(preset.choice);
  }, [preset]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!choice || status === 'submitting') return;
    setStatus('submitting');
    try {
      await hubSpotService.submitDeckInterest(choice, email.trim(), wantsUpdates);
      try {
        window.localStorage.setItem(STORAGE_KEY, choice);
      } catch {
        /* storage blocked: the acknowledgment still shows for this visit */
      }
      setRecorded(choice);
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="interest" tabIndex={-1} aria-labelledby="interest-heading" className="scroll-mt-20 focus:outline-none py-16 bg-gray-50">
      <div className="container max-w-3xl mx-auto px-6">
        <div className="text-center">
          <h2 id="interest-heading" className="text-3xl font-bold mb-4 text-gray-800">Help Us Decide What to Produce</h2>
          <p className="text-lg text-gray-700">
            We&apos;ve designed four possible editions of The Pawsitive Deck. Before production, we want to hear which ones
            you&apos;d actually purchase.
          </p>
        </div>

        <div className="card mt-10 p-6 sm:p-8">
          {recorded ? (
            <div role="status" className="text-center">
              <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-50 text-green-700" aria-hidden="true">
                <FeatherIcon name="check-circle" size={28} />
              </span>
              <p className="text-xl font-bold text-gray-800">Thank you — your interest is recorded.</p>
              <p className="mt-2 text-gray-700">
                You chose <span className="font-semibold">{labelFor(recorded)}</span>.
              </p>
              <p className="mt-4 text-sm text-gray-600">
                Changed your mind? Email{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-primary hover:underline">{CONTACT_EMAIL}</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <fieldset>
                <legend className="text-lg font-semibold text-gray-800">Which would you buy?</legend>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {INTEREST_OPTIONS.map((option) => (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition focus-within:ring-2 focus-within:ring-primary ${
                        choice === option.value ? 'border-primary bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                      } ${option.value === 'all' ? 'sm:col-span-2' : ''}`}
                    >
                      <input
                        type="radio"
                        name="deck-interest"
                        value={option.value}
                        checked={choice === option.value}
                        onChange={() => setChoice(option.value)}
                        className="h-4 w-4 accent-primary"
                        required
                      />
                      <span className="font-medium text-gray-800">
                        {option.label}
                        {option.value === 'all' && (
                          <span className="block text-sm font-normal text-gray-600">I&apos;d buy every edition above</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="mt-6">
                <label htmlFor="deck-interest-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  id="deck-interest-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT_CLASS}
                  aria-describedby="deck-interest-email-note"
                  required
                />
                <p id="deck-interest-email-note" className="mt-1 text-sm text-gray-500">
                  Required to vote, so we count one vote per person. We won&apos;t email you unless you tick the box below.
                </p>
                <label className="mt-4 flex items-start gap-3 text-gray-700">
                  <input
                    type="checkbox"
                    checked={wantsUpdates}
                    onChange={(e) => setWantsUpdates(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-primary"
                  />
                  <span>
                    Email me launch updates about The Pawsitive Deck.
                    <span className="block text-sm text-gray-500">Optional. We&apos;ll only use your email for these updates.</span>
                  </span>
                </label>
              </div>

              {status === 'error' && (
                <p role="alert" className="mt-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  We couldn&apos;t record your interest just now. Please try again, or email {CONTACT_EMAIL}.
                </p>
              )}

              <button
                type="submit"
                disabled={!open || !choice || !email.trim() || status === 'submitting'}
                className="btn-primary mt-6 w-full px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'submitting' ? 'Submitting…' : 'Submit My Interest'}
              </button>
              {!open && (
                <p className="mt-3 text-center text-sm text-gray-600">
                  Interest submissions open soon. Until then, tell us your pick at{' '}
                  <a href={`mailto:${CONTACT_EMAIL}?subject=The%20Pawsitive%20Deck`} className="font-medium text-primary hover:underline">
                    {CONTACT_EMAIL}
                  </a>
                  .
                </p>
              )}
              <p className="mt-4 text-center text-xs text-gray-500">
                This is a non-binding show of interest — not an order, a reservation, or a commitment to buy. No payment is taken.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default InterestForm;
